/**
 * Car.info Filter Modal
 * Filter dialog for filtering vehicles based on Car.info data
 * Visar alla aktiva fält direkt - användaren väljer värden för de fält de vill filtrera på
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon, FunnelIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useTranslation, useLanguage } from '@/store/languageStore';
import api from '@/services/api/client';
import toast from 'react-hot-toast';

interface FilterField {
  field_key: string;
  display_name_sv: string;
  display_name_en: string;
  field_type: 'boolean' | 'string' | 'number';
  category?: string;
}

interface CarInfoFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSearch: (vehicleIds: number[]) => void;
}

const CarInfoFilterModal: React.FC<CarInfoFilterModalProps> = ({ isOpen, onClose, onSearch }) => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [fields, setFields] = useState<FilterField[]>([]);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [fieldOptions, setFieldOptions] = useState<Record<string, string[]>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingKeys, setLoadingKeys] = useState<Record<string, boolean>>({});
  const valueQueueRef = useRef<string[]>([]);
  const queueRunningRef = useRef(false);
  const loadedKeysRef = useRef<Set<string>>(new Set());
  const cancelledRef = useRef(false);

  const drainValueQueue = useCallback(async () => {
    if (queueRunningRef.current) return;
    queueRunningRef.current = true;
    while (valueQueueRef.current.length > 0 && !cancelledRef.current) {
      const fieldKey = valueQueueRef.current.shift();
      if (!fieldKey || loadedKeysRef.current.has(fieldKey)) continue;
      setLoadingKeys((prev) => ({ ...prev, [fieldKey]: true }));
      try {
        const response = await api.get(`/api/carinfo-filter/values/${fieldKey}`);
        if (cancelledRef.current) break;
        if (response.data?.success) {
          loadedKeysRef.current.add(fieldKey);
          setFieldOptions((prev) => ({
            ...prev,
            [fieldKey]: response.data.data || [],
          }));
        }
      } catch (error) {
        console.error(`Failed to load values for ${fieldKey}:`, error);
      } finally {
        setLoadingKeys((prev) => {
          const next = { ...prev };
          delete next[fieldKey];
          return next;
        });
      }
    }
    queueRunningRef.current = false;
  }, []);

  const enqueueFieldValues = useCallback((fieldKey: string) => {
    if (loadedKeysRef.current.has(fieldKey)) return;
    if (valueQueueRef.current.includes(fieldKey)) return;
    valueQueueRef.current.push(fieldKey);
    void drainValueQueue();
  }, [drainValueQueue]);

  useEffect(() => {
    if (!isOpen) {
      cancelledRef.current = true;
      valueQueueRef.current = [];
      return;
    }

    cancelledRef.current = false;
    const loadFields = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/api/carinfo-filter/fields');
        if (cancelledRef.current) return;
        if (response.data?.success) {
          setFields(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to load filter fields:', error);
        toast.error(t('carInfoFilter.loadError'));
      } finally {
        if (!cancelledRef.current) setIsLoading(false);
      }
    };

    void loadFields();
    return () => {
      cancelledRef.current = true;
      valueQueueRef.current = [];
    };
  }, [isOpen, t]);

  // Get display name for field
  const getDisplayName = (field: FilterField): string => {
    return language === 'sv' ? field.display_name_sv : field.display_name_en;
  };

  // Update filter value
  const updateFilterValue = (fieldKey: string, value: string) => {
    setFilterValues(prev => ({
      ...prev,
      [fieldKey]: value
    }));
  };

  // Handle search
  const handleSearch = async () => {
    // Get fields with values
    const activeFilters = Object.entries(filterValues)
      .filter(([, value]) => value !== '' && value !== undefined)
      .map(([field, value]) => ({
        field,
        operator: '=',
        value
      }));

    if (activeFilters.length === 0) {
      toast.error(t('carInfoFilter.selectFilterValue'));
      return;
    }

    setIsSearching(true);
    try {
      const response = await api.post('/api/carinfo-filter/search', {
        filters: activeFilters
      });

      if (response.data?.success) {
        const vehicleIds = response.data.data.map((v: any) => v.vehicle_id);
        toast.success(t('carInfoFilter.vehiclesFoundCount').replace('{count}', String(response.data.count)));
        onSearch(vehicleIds);
        onClose();
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error(t('carInfoFilter.searchFailed'));
    } finally {
      setIsSearching(false);
    }
  };

  // Clear all filter values
  const handleClear = () => {
    setFilterValues({});
  };

  // Count active filters
  const activeFilterCount = Object.values(filterValues).filter(v => v !== '' && v !== undefined).length;

  // Filter fields by search query
  const filteredFields = fields.filter(field => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      field.display_name_sv.toLowerCase().includes(query) ||
      field.display_name_en.toLowerCase().includes(query) ||
      field.field_key.toLowerCase().includes(query)
    );
  });

  // Group fields by category
  const groupedFields = filteredFields.reduce((acc, field) => {
    const category = field.category || t('carInfoFilter.otherCategory');
    if (!acc[category]) acc[category] = [];
    acc[category].push(field);
    return acc;
  }, {} as Record<string, FilterField[]>);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop - halvtransparent */}
      <div 
        className="absolute inset-0 bg-black/50"
        onClick={onClose}
      />
      
      {/* Modal - solid bakgrund */}
      <div className="relative bg-bgElevated rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <FunnelIcon className="h-6 w-6 text-primary" />
            <h2 className="text-xl font-semibold text-textDefault">
              {t('carInfoFilter.title') || 'Car.info Filter'}
            </h2>
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 bg-primary text-white text-sm rounded-full">
                {activeFilterCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-hover transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-textSecondary" />
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-border">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-textSecondary" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('carInfoFilter.searchFields')}
              className="w-full pl-10 pr-4 py-2 bg-bgLight border border-border rounded-lg text-textDefault focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : fields.length === 0 ? (
            <div className="text-center py-12 text-textSecondary">
              <FunnelIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{t('carInfoFilter.noFiltersConfigured')}</p>
              <p className="text-sm mt-1">{t('carInfoFilter.enableFiltersHint')}</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedFields).sort().map(([category, categoryFields]) => (
                <div key={category}>
                  <h3 className="text-sm font-semibold text-textSecondary uppercase tracking-wide mb-3">
                    {category}
                  </h3>
                  <div className="space-y-3">
                    {categoryFields.map(field => {
                      const options = fieldOptions[field.field_key];
                      const isValuesLoading = Boolean(loadingKeys[field.field_key]);
                      const currentValue = filterValues[field.field_key] || '';
                      const showSelect = options === undefined || options.length > 0 || isValuesLoading;
                      
                      return (
                        <div key={field.field_key} className="flex items-center gap-4">
                          <label className="w-1/3 text-sm font-medium text-textDefault truncate" title={getDisplayName(field)}>
                            {getDisplayName(field)}
                          </label>
                          <div className="flex-1">
                            {showSelect ? (
                              <select
                                value={currentValue}
                                onFocus={() => enqueueFieldValues(field.field_key)}
                                onChange={(e) => updateFilterValue(field.field_key, e.target.value)}
                                className={`w-full px-3 py-2 bg-surface border rounded-lg text-textDefault focus:ring-2 focus:ring-primary focus:border-primary ${
                                  currentValue ? 'border-primary bg-primary/5' : 'border-border'
                                }`}
                              >
                                <option value="">
                                  {isValuesLoading
                                    ? t('carInfoFilter.loadingValues')
                                    : t('carInfoFilter.allOption')}
                                </option>
                                {(options || []).map(val => (
                                  <option key={val} value={val}>{val}</option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type={field.field_type === 'number' ? 'number' : 'text'}
                                value={currentValue}
                                onFocus={() => enqueueFieldValues(field.field_key)}
                                onChange={(e) => updateFilterValue(field.field_key, e.target.value)}
                                placeholder={t('carInfoFilter.enterValue')}
                                className={`w-full px-3 py-2 bg-surface border rounded-lg text-textDefault focus:ring-2 focus:ring-primary focus:border-primary ${
                                  currentValue ? 'border-primary bg-primary/5' : 'border-border'
                                }`}
                              />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-hover/30">
          <button
            onClick={handleClear}
            disabled={activeFilterCount === 0}
            className="px-4 py-2 text-textSecondary hover:text-textDefault hover:bg-hover rounded-lg transition-colors disabled:opacity-50"
          >
            {t('carInfoFilter.clear')} ({activeFilterCount})
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-textSecondary hover:text-textDefault hover:bg-hover rounded-lg transition-colors"
            >
              {t('carInfoFilter.cancel')}
            </button>
            <button
              onClick={handleSearch}
              disabled={isSearching || activeFilterCount === 0}
              className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50"
            >
              {isSearching ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                  <span>{t('carInfoFilter.searching')}</span>
                </>
              ) : (
                <span>{t('carInfoFilter.searchButton')}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarInfoFilterModal;
